import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  keyframes,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";

import { SimpleCard } from "app/components";
import axios from "app/utils/axios.js";
import apiPaths from "app/utils/apiPaths";
import { extractHttpErrorMessage } from "app/utils/httpError";
import C1ListPanel from "app/c1component/C1ListPanel";

const STEP_ORDER = [
  { id: "VERIFY_EMAIL", label: "Email verified" },
  { id: "CREATE_ACCOUNT", label: "Hospital account created" },
  { id: "CREATE_APPLICATION", label: "Applications configured" },
  { id: "CREATE_SUPER_ADMIN", label: "Administrator account created" },
  { id: "CLONE_ROLES", label: "Roles assigned" },
  { id: "CLONE_PERMISSIONS", label: "Permissions configured" },
  { id: "INITIALIZE_SETUP", label: "Hospital setup initialized" },
  { id: "SEND_EMAIL", label: "Sending login credentials" },
];

const STEP_FAILURE_LABELS = {
  VERIFY_EMAIL: "Verify Email",
  CREATE_ACCOUNT: "Create Hospital Account",
  CREATE_APPLICATION: "Configure Application",
  CREATE_SUPER_ADMIN: "Create Administrator",
  CLONE_ROLES: "Assign Roles",
  CLONE_PERMISSIONS: "Configure Permissions",
  INITIALIZE_SETUP: "Initialize Hospital Setup",
  SEND_EMAIL: "Send Login Credentials",
  DONE: "Complete Provisioning",
};

const pulse = keyframes`
  0% { transform: scale(0.92); opacity: 0.7; }
  50% { transform: scale(1); opacity: 1; }
  100% { transform: scale(0.92); opacity: 0.7; }
`;

const COLORS = {
  ink: "#0F172A",
  muted: "#64748B",
  line: "#E2E8F0",
  surface: "#F8FAFC",
  accent: "#0B6E4F",
  accentSoft: "#D1FAE5",
  active: "#0369A1",
  activeSoft: "#E0F2FE",
  danger: "#B91C1C",
  dangerSoft: "#FEE2E2",
};

function resolveApiBase() {
  const raw = window._env_?.REACT_APP_API_ENDPOINT || "http://localhost:9090/";
  return String(raw).endsWith("/") ? String(raw) : `${raw}/`;
}

function StepIcon({ state }) {
  if (state === "completed") {
    return (
      <CheckCircleRoundedIcon
        sx={{ color: COLORS.accent, fontSize: 26 }}
        aria-label="completed"
      />
    );
  }
  if (state === "active") {
    return (
      <Box
        sx={{
          width: 26,
          height: 26,
          display: "grid",
          placeItems: "center",
          animation: `${pulse} 1.4s ease-in-out infinite`,
        }}
      >
        <CircularProgress size={18} thickness={5} sx={{ color: COLORS.active }} />
      </Box>
    );
  }
  if (state === "failed") {
    return (
      <ErrorOutlineRoundedIcon
        sx={{ color: COLORS.danger, fontSize: 26 }}
        aria-label="failed"
      />
    );
  }
  return (
    <RadioButtonUncheckedIcon
      sx={{ color: "#CBD5E1", fontSize: 24 }}
      aria-label="pending"
    />
  );
}

function ProvisioningTimeline({ stepStates }) {
  return (
    <Stack spacing={0} sx={{ mt: 1 }}>
      {STEP_ORDER.map((step, index) => {
        const state = stepStates[step.id] || "pending";
        const isActive = state === "active";
        const label =
          state === "active" && step.id === "SEND_EMAIL"
            ? "Sending login credentials..."
            : step.label;

        return (
          <motion.div
            key={step.id}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.04, duration: 0.35 }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                py: 1.15,
                px: 1.25,
                borderRadius: 2,
                backgroundColor: isActive ? COLORS.activeSoft : "transparent",
                transition: "background-color 0.25s ease",
              }}
            >
              <Box sx={{ pt: "2px" }}>
                <StepIcon state={state} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: '"Inter", "Segoe UI", sans-serif',
                    fontSize: "0.98rem",
                    fontWeight: isActive ? 600 : state === "completed" ? 500 : 400,
                    color:
                      state === "failed"
                        ? COLORS.danger
                        : state === "pending"
                          ? COLORS.muted
                          : COLORS.ink,
                    textDecoration: state === "completed" ? "none" : "none",
                  }}
                >
                  {state === "completed" ? `✓ ${label}` : label}
                </Typography>
              </Box>
            </Box>
            {index < STEP_ORDER.length - 1 ? (
              <Box
                sx={{
                  ml: "12px",
                  width: 2,
                  height: 10,
                  backgroundColor: COLORS.line,
                  borderRadius: 1,
                }}
              />
            ) : null}
          </motion.div>
        );
      })}
    </Stack>
  );
}

function SuccessPanel({ email, tenantUrl, onLogin }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Stack spacing={2.5} alignItems="center" textAlign="center">
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            backgroundColor: COLORS.accentSoft,
            display: "grid",
            placeItems: "center",
          }}
        >
          <CheckCircleRoundedIcon sx={{ fontSize: 36, color: COLORS.accent }} />
        </Box>
        <Typography
          sx={{
            fontFamily: '"Merriweather", Georgia, serif',
            fontSize: { xs: "1.6rem", sm: "1.85rem" },
            fontWeight: 600,
            color: COLORS.ink,
            letterSpacing: "-0.02em",
          }}
        >
          Your hospital is ready
        </Typography>
        <Typography sx={{ color: COLORS.muted, maxWidth: 420, lineHeight: 1.6 }}>
          Hospital registration successful.
        </Typography>
        {tenantUrl ? (
          <Box
            sx={{
              width: "100%",
              maxWidth: 420,
              p: 2,
              borderRadius: 2,
              backgroundColor: COLORS.surface,
              border: `1px solid ${COLORS.line}`,
              textAlign: "left",
            }}
          >
            <Typography sx={{ color: COLORS.muted, fontSize: "0.85rem", mb: 0.5 }}>
              Your application URL
            </Typography>
            <Typography
              component="a"
              href={tenantUrl}
              sx={{
                fontWeight: 700,
                color: COLORS.accent,
                wordBreak: "break-all",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {tenantUrl}
            </Typography>
          </Box>
        ) : null}
        <Stack spacing={0.75} sx={{ width: "100%", maxWidth: 420, textAlign: "left" }}>
          {[
            "Administrator account created",
            "Applications configured",
            "Permissions assigned",
            "Hospital workspace initialized",
          ].map((item) => (
            <Typography
              key={item}
              sx={{ color: COLORS.ink, fontSize: "0.95rem", display: "flex", gap: 1 }}
            >
              <Box component="span" sx={{ color: COLORS.accent }}>
                ✓
              </Box>
              {item}
            </Typography>
          ))}
        </Stack>
        <Typography sx={{ color: COLORS.muted, pt: 1 }}>
          Login credentials have been sent to:
        </Typography>
        <Typography sx={{ fontWeight: 600, color: COLORS.ink, wordBreak: "break-all" }}>
          {email || "your registered email"}
        </Typography>
        <Button
          variant="contained"
          onClick={onLogin}
          sx={{
            mt: 1,
            px: 4,
            py: 1.25,
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: COLORS.accent,
            "&:hover": { backgroundColor: "#095c42" },
          }}
        >
          Go to Login
        </Button>
      </Stack>
    </motion.div>
  );
}

function FailurePanel({ failedStep, reason, onRetry, onSupport }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
    >
      <Stack spacing={2.25} alignItems="center" textAlign="center">
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            backgroundColor: COLORS.dangerSoft,
            display: "grid",
            placeItems: "center",
          }}
        >
          <ErrorOutlineRoundedIcon sx={{ fontSize: 34, color: COLORS.danger }} />
        </Box>
        <Typography
          sx={{
            fontFamily: '"Merriweather", Georgia, serif',
            fontSize: "1.55rem",
            fontWeight: 600,
            color: COLORS.ink,
          }}
        >
          Provisioning could not be completed
        </Typography>
        <Box
          sx={{
            width: "100%",
            maxWidth: 440,
            textAlign: "left",
            p: 2,
            borderRadius: 2,
            backgroundColor: COLORS.surface,
            border: `1px solid ${COLORS.line}`,
          }}
        >
          <Typography sx={{ color: COLORS.muted, fontSize: "0.85rem", mb: 0.5 }}>
            Step failed
          </Typography>
          <Typography sx={{ fontWeight: 600, color: COLORS.ink, mb: 1.5 }}>
            {STEP_FAILURE_LABELS[failedStep] || failedStep || "Provisioning"}
          </Typography>
          <Typography sx={{ color: COLORS.muted, fontSize: "0.85rem", mb: 0.5 }}>
            Reason
          </Typography>
          <Typography sx={{ color: COLORS.ink, lineHeight: 1.5 }}>
            {reason || "An unexpected error occurred during provisioning."}
          </Typography>
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ pt: 1 }}>
          <Button
            variant="contained"
            onClick={onRetry}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              backgroundColor: COLORS.ink,
              "&:hover": { backgroundColor: "#1e293b" },
            }}
          >
            Retry
          </Button>
          <Button
            variant="outlined"
            onClick={onSupport}
            sx={{ textTransform: "none", fontWeight: 600, borderColor: COLORS.line, color: COLORS.ink }}
          >
            Contact Support
          </Button>
        </Stack>
      </Stack>
    </motion.div>
  );
}

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = useMemo(
    () => new URLSearchParams(location.search).get("v3uref"),
    [location.search],
  );

  const [phase, setPhase] = useState("boot"); // boot | progress | success | failure
  const [email, setEmail] = useState("");
  const [tenantUrl, setTenantUrl] = useState(null);
  const [stepStates, setStepStates] = useState(() =>
    Object.fromEntries(STEP_ORDER.map((s) => [s.id, "pending"])),
  );
  const [failedStep, setFailedStep] = useState(null);
  const [failureReason, setFailureReason] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const sourceRef = useRef(null);
  const closedRef = useRef(false);

  const closeSource = useCallback(() => {
    if (sourceRef.current) {
      sourceRef.current.close();
      sourceRef.current = null;
    }
  }, []);

  const applyEvent = useCallback((event) => {
    if (!event || !event.step) return;
    const { step, status, message } = event;
    if (event.tenantUrl) {
      setTenantUrl(event.tenantUrl);
    }

    if (status === "FAILED") {
      setFailedStep(step);
      setFailureReason(message);
      setStepStates((prev) => ({
        ...prev,
        [step]: "failed",
      }));
      setPhase("failure");
      closeSource();
      return;
    }

    if (step === "DONE" && status === "SUCCESS") {
      setStepStates((prev) => {
        const next = { ...prev };
        STEP_ORDER.forEach((s) => {
          next[s.id] = "completed";
        });
        return next;
      });
      setPhase("success");
      closeSource();
      return;
    }

    setStepStates((prev) => {
      const next = { ...prev };
      if (status === "COMPLETED") {
        next[step] = "completed";
        const idx = STEP_ORDER.findIndex((s) => s.id === step);
        const upcoming = STEP_ORDER[idx + 1];
        if (upcoming && next[upcoming.id] === "pending") {
          next[upcoming.id] = "active";
        }
      } else if (status === "IN_PROGRESS") {
        next[step] = "active";
        STEP_ORDER.forEach((s) => {
          if (s.id !== step && next[s.id] === "active") {
            next[s.id] = "completed";
          }
        });
      }
      return next;
    });
  }, [closeSource]);

  const openProgressStream = useCallback(
    (registrationId) => {
      closeSource();
      const url = `${resolveApiBase()}${apiPaths.PROVISION_STATUS(registrationId)}`;
      const source = new EventSource(url, { withCredentials: true });
      sourceRef.current = source;

      const onProgress = (evt) => {
        try {
          const payload = JSON.parse(evt.data);
          applyEvent(payload);
        } catch (err) {
          // ignore malformed frames
        }
      };

      source.addEventListener("progress", onProgress);
      source.onmessage = onProgress;
      source.onerror = () => {
        if (closedRef.current) return;
        // Keep timeline visible; stream may reconnect briefly during proxy handoff.
      };
    },
    [applyEvent, closeSource],
  );

  const startVerification = useCallback(async () => {
    closedRef.current = false;
    setPhase("progress");
    setFailedStep(null);
    setFailureReason(null);
    setStepStates(
      Object.fromEntries(
        STEP_ORDER.map((s, i) => [s.id, i === 0 ? "active" : "pending"]),
      ),
    );

    if (!token) {
      setFailedStep("VERIFY_EMAIL");
      setFailureReason("Token is missing.");
      setPhase("failure");
      return;
    }

    try {
      const response = await axios.get(apiPaths.VERIFY_EMAIL, {
        params: { v3uref: token },
      });
      const registrationId = response?.data?.registrationId;
      const workEmail = response?.data?.email;
      if (workEmail) setEmail(workEmail);
      if (!registrationId) {
        throw new Error("Provisioning session could not be started.");
      }
      openProgressStream(registrationId);
    } catch (err) {
      setFailedStep("VERIFY_EMAIL");
      setFailureReason(
        extractHttpErrorMessage(err, { generic: "Verification failed." }),
      );
      setStepStates((prev) => ({ ...prev, VERIFY_EMAIL: "failed" }));
      setPhase("failure");
    }
  }, [openProgressStream, token]);

  useEffect(() => {
    startVerification();
    return () => {
      closedRef.current = true;
      closeSource();
    };
  }, [retryNonce]); // eslint-disable-line react-hooks/exhaustive-deps -- retryNonce drives restarts

  const handleRetry = () => {
    closedRef.current = true;
    closeSource();
    setRetryNonce((n) => n + 1);
  };

  const handleSupport = () => {
    window.location.href = "mailto:support@cheritto.com?subject=Hospital%20provisioning%20help";
  };

  return (
    <C1ListPanel>
      <Stack spacing={3}>
        <SimpleCard>
          <Box
            sx={{
              textAlign: "left",
              px: { xs: 2, sm: 4 },
              py: { xs: 3, sm: 4 },
              maxWidth: 560,
              mx: "auto",
            }}
          >
            <AnimatePresence mode="wait">
              {phase === "success" ? (
                <SuccessPanel
                  key="success"
                  email={email}
                  tenantUrl={tenantUrl}
                  onLogin={() => {
                    if (tenantUrl) {
                      window.location.href = `${tenantUrl.replace(/\/$/, "")}/session/signin`;
                    } else {
                      navigate("/session/signin");
                    }
                  }}
                />
              ) : phase === "failure" ? (
                <FailurePanel
                  key="failure"
                  failedStep={failedStep}
                  reason={failureReason}
                  onRetry={handleRetry}
                  onSupport={handleSupport}
                />
              ) : (
                <motion.div
                  key="progress"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"Merriweather", Georgia, serif',
                      fontSize: { xs: "1.55rem", sm: "1.85rem" },
                      fontWeight: 600,
                      color: COLORS.ink,
                      letterSpacing: "-0.02em",
                      mb: 0.75,
                    }}
                  >
                    Building your hospital workspace
                  </Typography>
                  <Typography sx={{ color: COLORS.muted, mb: 2.5, lineHeight: 1.6 }}>
                    This usually takes less than 30 seconds.
                  </Typography>
                  <ProvisioningTimeline stepStates={stepStates} />
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </SimpleCard>
      </Stack>
    </C1ListPanel>
  );
};

export default VerifyEmail;
